import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { APT } from "@/constants/testIds";
import {
  DEFAULT_APPOINTMENT_SMS_MESSAGE,
  getAppointmentSmsPreview,
  sendAppointmentSmsTest,
} from "@/lib/appointmentApi";

export default function AppointmentSmsDialog({ appointment, open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState(DEFAULT_APPOINTMENT_SMS_MESSAGE);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !appointment?.id) {
      setPreview(null);
      setMessage(DEFAULT_APPOINTMENT_SMS_MESSAGE);
      setError("");
      setResult(null);
      setLoading(false);
      setSending(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setResult(null);
    getAppointmentSmsPreview(appointment.id)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setMessage(data?.defaultMessage || DEFAULT_APPOINTMENT_SMS_MESSAGE);
        if (data?.reason && !data?.canSend) setError(data.reason);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Unable to load appointment SMS details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, appointment?.id]);

  const send = async (event) => {
    event.preventDefault();
    if (!appointment?.id || sending || !preview?.canSend) return;
    setSending(true);
    setError("");
    setResult(null);
    try {
      const data = await sendAppointmentSmsTest(appointment.id, { message: message.trim() });
      setResult(data);
      toast.success("Test SMS accepted by AWS.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text = typeof detail === "string" ? detail : "AWS SMS send failed.";
      setError(text);
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid={APT.smsDialog} className="z-[60] max-w-lg">
        <DialogHeader>
          <DialogTitle>Send appointment SMS</DialogTitle>
          <DialogDescription>
            TEST / SANDBOX only. This is not patient messaging. The first test
            message must not include PHI. AWS is not contacted until you click
            Send Test SMS.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={send} className="space-y-4">
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Patient</dt>
              <dd className="mt-0.5 text-sm font-medium text-slate-900" data-testid={APT.smsPatient}>
                {preview?.patientDisplay || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Mobile phone</dt>
              <dd className="mt-0.5 text-sm text-slate-900" data-testid={APT.smsMobile}>
                {preview?.mobilePhoneMasked || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Appointment date</dt>
              <dd className="mt-0.5 text-sm text-slate-900" data-testid={APT.smsDate}>
                {preview?.appointmentDate || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Appointment time</dt>
              <dd className="mt-0.5 text-sm text-slate-900" data-testid={APT.smsTime}>
                {preview?.appointmentTime || "—"}
              </dd>
            </div>
          </dl>

          {preview ? (
            <p
              data-testid={APT.smsConsentStatus}
              className={`rounded-md border px-3 py-2 text-xs ${
                preview.smsConsent?.allowsSend
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-amber-200 bg-amber-50 text-amber-950"
              }`}
            >
              {preview.smsConsent?.allowsSend
                ? `SMS consent: opted in${
                    preview.smsConsent.consentedAt ? ` · ${preview.smsConsent.consentedAt}` : ""
                  }${preview.smsConsent.method === "staff_assisted" ? " · staff-assisted" : ""}`
                : `SMS blocked: ${
                    preview.smsConsent?.reason ||
                    preview.reason ||
                    "This patient has not opted in to SMS"
                  }. Record consent on the patient chart under Front Desk → Demographics.`}
            </p>
          ) : null}

          {preview?.usingSandboxSimulator ? (
            <p
              data-testid={APT.smsSendDestination}
              className="rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-900"
            >
              This SANDBOX send uses the AWS simulator destination{" "}
              {preview.sendDestinationMasked || "+*******7755"}, not the patient
              mobile above. Simulator mode does not bypass SMS consent.
            </p>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor={APT.smsMessage}>Message</Label>
            <Textarea
              id={APT.smsMessage}
              data-testid={APT.smsMessage}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
            />
            <p className="text-xs text-slate-500">
              Default test text contains no patient name, MRN, diagnosis, or
              appointment details.
            </p>
          </div>

          {error ? (
            <div
              data-testid={APT.smsResult}
              className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : null}

          {result ? (
            <div
              data-testid={APT.smsResult}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
            >
              <p className="font-medium">AWS accepted the test SMS.</p>
              <p className="mt-1">Message ID: {result.messageId || "not returned"}</p>
              <p className="mt-1">Destination: {result.destination}</p>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" data-testid={APT.smsCancel} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid={APT.smsSend}
              className="bg-[#0d5c4d] hover:bg-[#0a4b3f]"
              disabled={sending || loading || !preview?.canSend}
            >
              {sending ? "Sending…" : "Send Test SMS"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
